/**
 * **塞回异常的"后续"信息** —— 从一份 PlayerView + 落点索引推导两件事：
 *
 * 1. 这张 [异常] 会成为**接下来第几张被抽到**的牌；
 * 2. 若之后**没有人洗乱抽牌堆**，大概会轮到**谁**抽到它。
 *
 * ⚠️ 纯函数（不碰 Pixi、不读时钟），放在 analysis/ 供 UI 用。
 *
 * 位置约定与 `DrawPile` / `TableIntent.insert` 完全一致：
 * 索引 0 = 牌堆**底**，`drawPileCount` = 牌堆**顶**（塞回后立刻是下一张）。
 */

import type { PlayerId } from '../core/ids';
import type { PlayerView } from '../core/project';

export interface InsertOutcome {
  /** 从下一次抽牌算起，这张 [异常] 是**第几张**被抽到（1 = 下一张） */
  readonly drawsUntil: number;
  /** 若无人洗牌，预计抽到它的玩家；推不出来（没有回合信息）时 null */
  readonly drawer: PlayerId | null;
  /** 对应玩家名（方便 UI，不用再查一次）；无则 null */
  readonly drawerName: string | null;
}

/**
 * 算出塞回的后续。
 *
 * - `drawsUntil`：塞在位次 `insertIndex`（0..N）后，它上面还有 `N - insertIndex`
 *   张，所以它是第 `N - insertIndex + 1` 张被抽到。
 * - `drawer`：从**当前回合玩家紧接着的未来回合**开始数。当前玩家若因被攻击
 *   还欠着额外回合（`turnsRemaining - 1`），那几张先归他；之后按座位序在
 *   **存活玩家**里循环。
 *
 * ⚠️ 这是"无人洗牌 / 无额外抽牌效果"的**估计**，面板上要这么写。
 */
export function insertOutcome(view: PlayerView, insertIndex: number): InsertOutcome {
  const count = view.drawPileCount;
  const i = Math.max(0, Math.min(count, insertIndex));
  const drawsUntil = Math.max(1, count - i + 1);

  const turn = view.turn;
  if (turn === null) return { drawsUntil, drawer: null, drawerName: null };

  const nameOf = (id: PlayerId): string | null =>
    view.players.find((p) => p.id === id)?.name ?? null;

  // 当前玩家还会再行动（并把牌抽走）的次数：turnsRemaining 含正在进行的这一回合
  const oweCurrent = Math.max(0, turn.turnsRemaining - 1);
  let d = drawsUntil;
  if (d <= oweCurrent) return { drawsUntil, drawer: turn.player, drawerName: nameOf(turn.player) };

  // 之后的回合按座位序、只在存活玩家之间轮
  d -= oweCurrent;
  const order = view.players.map((p) => p.id);
  const start = order.indexOf(turn.player);
  const alive: PlayerId[] = [];
  for (let step = 1; step < order.length; step++) {
    const idx = start < 0 ? step - 1 : (start + step) % order.length;
    const id = order[idx];
    if (id === undefined) continue;
    const p = view.players[idx];
    if (p?.alive === true) alive.push(id);
  }
  if (alive.length === 0) return { drawsUntil, drawer: null, drawerName: null };
  const drawer = alive[(d - 1) % alive.length] ?? null;
  return { drawsUntil, drawer, drawerName: drawer === null ? null : nameOf(drawer) };
}
